import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2, Users, Gauge, AlertTriangle, Banknote,
  TrendingUp, ArrowRight, Activity
} from "lucide-react";
import { fetchOverview, fetchTenants, isNearLimit } from "../services/api";
import { formatZAR } from "../services/plans";
import { usePacks, PACK_GROUP_LABELS, PACK_GROUP_ORDER } from "../services/packs";
import type { PlatformOverview, Tenant } from "../types";
import { StatusPill } from "../components/StatusPill";
import { PackTag } from "../components/PackTag";
import { usePageTitle } from "../hooks/usePageTitle";

export function DashboardPage() {
  usePageTitle("Platform pulse");
  const [overview, setOverview] = useState<PlatformOverview | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const { bySlug } = usePacks();

  useEffect(() => {
    fetchOverview().then(setOverview);
    fetchTenants().then(setTenants);
  }, []);

  if (!overview) return <div className="muted">Loading…</div>;

  // Tenants grouped by package family (Comms / Field / Combined / Enterprise).
  const byGroup = PACK_GROUP_ORDER.map((g) => ({
    key: g as string,
    label: PACK_GROUP_LABELS[g],
    count: tenants.filter((t) => t.pack && bySlug[t.pack]?.group === g).length
  }));

  const recent = [...tenants].sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt)).slice(0, 5);
  const needsAttention = tenants.filter((t) =>
    t.status === "suspended" || t.billing.status === "past_due" || isNearLimit(t)
  ).slice(0, 6);
  const maxTrend = Math.max(1, ...overview.signupTrend.map((p) => p.count));

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Overview</span>
          <h1>Platform pulse</h1>
          <p className="muted">A live snapshot of every customer running on Orbit.</p>
        </div>
        <div className="page-actions">
          <Link to="/companies" className="btn btn-ghost">
            All companies <ArrowRight size={14} />
          </Link>
        </div>
      </header>

      {/* KPI row */}
      <div className="grid-4">
        <div className="card kpi">
          <div className="kpi-icon"><Building2 size={18} /></div>
          <h3>Active tenants</h3>
          <div className="value tnum">{overview.tenantsActive}</div>
          <div className="delta up"><TrendingUp size={12} /> {overview.tenantsTotal} total</div>
        </div>
        <div className="card kpi">
          <div className="kpi-icon" style={{ background: "var(--success-soft)", color: "var(--success)" }}>
            <Banknote size={18} />
          </div>
          <h3>Monthly recurring revenue</h3>
          <div className="value tnum">{formatZAR(overview.mrrCents)}</div>
          <div className="delta up"><TrendingUp size={12} /> based on active plans</div>
        </div>
        <div className="card kpi">
          <div className="kpi-icon" style={{ background: "var(--warn-soft)", color: "var(--warn)" }}>
            <Gauge size={18} />
          </div>
          <h3>Near plan limits</h3>
          <div className="value tnum">{overview.tenantsNearLimit}</div>
          <div className="delta"><AlertTriangle size={12} /> upsell opportunities</div>
        </div>
        <div className="card kpi">
          <div className="kpi-icon" style={{ background: "var(--danger-soft)", color: "var(--danger)" }}>
            <Activity size={18} />
          </div>
          <h3>Inactive 14+ days</h3>
          <div className="value tnum">{overview.tenantsAtRisk}</div>
          <div className="delta down"><AlertTriangle size={12} /> churn risk</div>
        </div>
      </div>

      {/* Mid row */}
      <div className="grid-2">
        <div className="card">
          <div className="section-head">
            <h2><Users size={16} /> Tenants by package</h2>
            <Link to="/companies" className="muted">View all →</Link>
          </div>
          <div className="col" style={{ gap: 14 }}>
            {byGroup.map((row) => {
              const pct = (row.count / Math.max(1, overview.tenantsTotal)) * 100;
              return (
                <div key={row.key} className="meter-row">
                  <div className="meter-label">{row.label}</div>
                  <div className="meter"><span style={{ width: `${pct}%` }} /></div>
                  <div className="meter-value tnum">{row.count} tenants</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card">
          <div className="section-head">
            <h2><TrendingUp size={16} /> Signups, last 30 days</h2>
            <span className="muted">{overview.signupTrend.reduce((s, p) => s + p.count, 0)} new</span>
          </div>
          <div className="row" style={{ alignItems: "flex-end", gap: 4, height: 130 }}>
            {overview.signupTrend.map((p) => (
              <div
                key={p.date}
                title={`${p.date} • ${p.count}`}
                style={{
                  flex: 1,
                  minWidth: 0,
                  height: `${(p.count / maxTrend) * 100}%`,
                  minHeight: 4,
                  background: "var(--gradient-1)",
                  borderRadius: 4,
                  opacity: p.count === 0 ? 0.15 : 1
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Tenant tables */}
      <div className="grid-2">
        <div className="card" style={{ padding: 0 }}>
          <div className="section-head" style={{ padding: "16px 22px 0" }}>
            <h2>Recently added</h2>
            <Link to="/companies?filter=recent" className="muted">All →</Link>
          </div>
          <div className="table-wrap" style={{ border: 0, borderRadius: 0, background: "transparent" }}>
            <table className="table">
              <tbody>
                {recent.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <Link to={`/companies/${t.id}`} style={{ color: "var(--text)", fontWeight: 600 }}>
                        {t.name}
                      </Link>
                      <div className="muted" style={{ fontSize: 11.5 }}>{t.industry}</div>
                    </td>
                    <td><PackTag pack={t.pack} /></td>
                    <td><StatusPill status={t.status} /></td>
                    <td className="right muted nowrap">{relativeDate(t.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card" style={{ padding: 0 }}>
          <div className="section-head" style={{ padding: "16px 22px 0" }}>
            <h2>Needs attention</h2>
            <span className="muted">{needsAttention.length} items</span>
          </div>
          {needsAttention.length === 0 ? (
            <div className="empty-state">
              <h3>All clear</h3>
              <p>No tenants are near limits, suspended, or past due.</p>
            </div>
          ) : (
            <div className="table-wrap" style={{ border: 0, borderRadius: 0, background: "transparent" }}>
              <table className="table">
                <tbody>
                  {needsAttention.map((t) => {
                    const reason = t.status === "suspended"
                      ? "Suspended"
                      : t.billing.status === "past_due"
                      ? "Past due"
                      : "Near plan limit";
                    return (
                      <tr key={t.id}>
                        <td>
                          <Link to={`/companies/${t.id}`} style={{ color: "var(--text)", fontWeight: 600 }}>
                            {t.name}
                          </Link>
                          <div className="muted" style={{ fontSize: 11.5 }}>{(t.pack && bySlug[t.pack]?.label) || "No package"}</div>
                        </td>
                        <td>
                          <span className={`pill ${reason === "Past due" || reason === "Suspended" ? "danger" : "warn"}`}>
                            {reason}
                          </span>
                        </td>
                        <td className="right muted nowrap">{relativeDate(t.lastActiveAt ?? t.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function relativeDate(iso: string | null): string {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  const d = Math.floor(ms / 86400000);
  if (d === 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 30) return `${d} days ago`;
  if (d < 365) return `${Math.floor(d / 30)} mo ago`;
  return `${Math.floor(d / 365)} yr ago`;
}
